import { Injectable, signal } from '@angular/core';
import { Host } from './models/host.model';

const HOST_ID_STORAGE_KEY = 'dod-host-selected-host-id';

@Injectable()
export class HostContextService {
  private readonly _currentHost = signal<Host | null>(null);
  private readonly _availableHosts = signal<Host[]>([]);

  /**
   * Current selected host (read-only)
   */
  readonly currentHost = this._currentHost.asReadonly();

  /**
   * All available hosts for the user (read-only)
   */
  readonly availableHosts = this._availableHosts.asReadonly();

  /**
   * Set the current host and persist to localStorage
   */
  setCurrentHost(host: Host | null): void {
    this._currentHost.set(host);
    if (host) {
      localStorage.setItem(HOST_ID_STORAGE_KEY, host.id);
    } else {
      localStorage.removeItem(HOST_ID_STORAGE_KEY);
    }
  }

  /**
   * Update a host in both currentHost and availableHosts
   */
  updateHost(host: Host): void {
    // Update current host if it matches
    const current = this._currentHost();
    if (current?.id === host.id) {
      this._currentHost.set(host);
    }

    // Update in available hosts array
    const hosts = this._availableHosts();
    const updatedHosts = hosts.map((h) => (h.id === host.id ? host : h));
    this._availableHosts.set(updatedHosts);
  }

  /**
   * Set available hosts
   */
  setAvailableHosts(hosts: Host[]): void {
    this._availableHosts.set(hosts);
  }

  /**
   * Get the stored host ID from localStorage
   */
  getStoredHostId(): string | null {
    return localStorage.getItem(HOST_ID_STORAGE_KEY);
  }

  /**
   * Clear host context (on logout or when host is deleted)
   */
  clearContext(): void {
    this._currentHost.set(null);
    this._availableHosts.set([]);
    localStorage.removeItem(HOST_ID_STORAGE_KEY);
  }
}
